import { useRef, useState, useEffect, type JSX } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import clsx from 'clsx';
import { useSessionTabs } from '../hooks/useSessionTabs';

export default function SessionTabsBar(): JSX.Element {
  const { tabs, activeTabId, switchTab, closeTab } = useSessionTabs();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [showLeft, setShowLeft] = useState<boolean>(false);
  const [showRight, setShowRight] = useState<boolean>(false);

  // Check overflow for scroll arrows
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const check = (): void => {
      setShowLeft(el.scrollLeft > 0);
      setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };
    check();
    el.addEventListener('scroll', check);
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', check);
      ro.disconnect();
    };
  }, [tabs]);

  const scrollBy = (dir: number): void => {
    scrollRef.current?.scrollBy({ left: dir * 200, behavior: 'smooth' });
  };

  return (
    <div className="h-[36px] flex-shrink-0 bg-surface-1 border-t border-border-subtle flex items-center relative">
      {/* Left scroll arrow */}
      {showLeft && (
        <button
          onClick={() => scrollBy(-1)}
          className="absolute left-0 z-10 h-full px-1 bg-surface-1 border-r border-border-subtle
                     text-ink-muted hover:text-ink-primary transition-colors cursor-pointer"
          aria-label="Scroll tabs left"
        >
          <ChevronLeft size={12} />
        </button>
      )}

      {/* Scrollable tabs */}
      <div
        ref={scrollRef}
        className="flex-1 flex items-center overflow-x-auto scrollbar-none"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {tabs.map((tab) => {
          const isActive = tab.id === activeTabId;
          // Outer tab element is a div with role="button" so the close control
          // inside can be a real <button> (nesting a <button> inside a <button>
          // is invalid HTML and the previous onClick-only <span> tripped the
          // jsx-a11y/click-events-have-key-events rule).
          return (
            <div
              key={tab.id}
              data-tab-id={tab.id}
              role="button"
              tabIndex={0}
              aria-current={isActive ? 'page' : undefined}
              onClick={() => switchTab(tab.id)}
              onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  switchTab(tab.id);
                }
              }}
              className={clsx(
                'flex items-center gap-1.5 px-3 h-[36px] text-xs whitespace-nowrap border-b-2 transition-colors cursor-pointer select-none flex-shrink-0',
                isActive
                  ? 'border-accent text-accent bg-surface-2/50'
                  : 'border-transparent text-ink-muted hover:text-ink-secondary hover:bg-surface-3'
              )}
            >
              <span>{tab.label}</span>
              {!tab.pinned && (
                <button
                  type="button"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    closeTab(tab.id);
                  }}
                  onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => {
                    // Prevent the outer div's onKeyDown from also firing a switchTab.
                    if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
                  }}
                  className="ml-0.5 p-0.5 rounded hover:bg-surface-4 text-ink-muted hover:text-ink-primary transition-colors"
                  aria-label={`Close ${tab.label} tab`}
                >
                  <X size={10} />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Right scroll arrow */}
      {showRight && (
        <button
          onClick={() => scrollBy(1)}
          className="absolute right-0 z-10 h-full px-1 bg-surface-1 border-l border-border-subtle
                     text-ink-muted hover:text-ink-primary transition-colors cursor-pointer"
          aria-label="Scroll tabs right"
        >
          <ChevronRight size={12} />
        </button>
      )}
    </div>
  );
}
